import React, { useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { isZebraStickerPrinter, stickerElementToBase64 } from '../utils/thermalStickerPdf';

/**
 * DYMO line-stock sample rack sticker (2.3125" × 4").
 * Brand + style (+ optional material/description) with QR to supplierUrl.
 */
const SampleSticker = ({
  product,
  material = '',
  autoGeneratePdf = false,
  onPdfGenerated
}) => {
  const stickerRef = useRef(null);
  const generatingRef = useRef(false);

  const brand = String(product?.brand || '').trim();
  const styleNo = String(product?.styleNo || '').trim();
  const description = String(
    product?.shortDescription || product?.description || ''
  ).trim();
  const materialText = String(material || '').trim();
  const supplierUrl = String(product?.supplierUrl || '').trim();

  useEffect(() => {
    const generatePdfIfReady = async () => {
      if (
        !autoGeneratePdf ||
        !product ||
        !stickerRef.current ||
        !onPdfGenerated ||
        generatingRef.current
      ) {
        return;
      }

      try {
        generatingRef.current = true;
        const element = stickerRef.current;
        const zebra = isZebraStickerPrinter();
        const opt = {
          margin: 0,
          filename: `sample_sticker_${styleNo || product.id || 'rack'}.pdf`,
          image: { type: 'jpeg', quality: 1 },
          html2canvas: {
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('[data-sample-sticker]');
              if (clonedElement) {
                clonedElement.style.width = '2.3125in';
                clonedElement.style.height = '4in';
                clonedElement.style.backgroundColor = 'white';
                clonedElement.style.fontFamily = "'Montserrat', sans-serif";
                clonedElement.style.boxSizing = 'border-box';
                clonedElement.style.color = '#222';
              }
            }
          },
          jsPDF: {
            unit: 'in',
            format: [2.3125, 4],
            orientation: 'portrait',
            compress: true,
            precision: 16
          }
        };

        const base64data = await stickerElementToBase64(element, opt, { zebra });
        onPdfGenerated(base64data);
        generatingRef.current = false;
      } catch (error) {
        console.error('Error generating sample sticker PDF:', error);
        generatingRef.current = false;
        throw error;
      }
    };

    generatePdfIfReady();
  }, [autoGeneratePdf, product, material, onPdfGenerated, styleNo]);

  return (
    <div
      ref={stickerRef}
      data-sample-sticker
      style={{
        width: '2.3125in',
        height: '4in',
        backgroundColor: 'white',
        fontFamily: "'Montserrat', sans-serif",
        color: '#222',
        boxSizing: 'border-box',
        padding: '0.18in 0.1in 0.1in 0.1in',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          fontSize: '7pt',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          borderBottom: '1px solid #222',
          paddingBottom: '0.04in',
          marginBottom: '0.1in'
        }}
      >
        Sample Rack
      </div>

      <div
        style={{
          fontSize: '9pt',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: '0.04in',
          lineHeight: 1.15
        }}
      >
        {brand || '—'}
      </div>

      <div
        style={{
          fontSize: '28pt',
          fontWeight: 700,
          lineHeight: 1,
          marginBottom: '0.08in',
          wordBreak: 'break-word'
        }}
      >
        {styleNo || '—'}
      </div>

      {description ? (
        <div
          style={{
            fontSize: '8pt',
            lineHeight: 1.2,
            marginBottom: '0.08in',
            textTransform: 'uppercase'
          }}
        >
          {description}
        </div>
      ) : null}

      {materialText ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            borderTop: '1px solid #222',
            borderBottom: '1px solid #222',
            marginBottom: '0.1in'
          }}
        >
          <div
            style={{
              fontSize: '7pt',
              fontWeight: 600,
              textTransform: 'uppercase',
              padding: '0.04in 0.05in',
              borderRight: '1px solid #222'
            }}
          >
            Material
          </div>
          <div
            style={{
              fontSize: '10pt',
              fontWeight: 600,
              textTransform: 'uppercase',
              padding: '0.04in 0.05in',
              lineHeight: 1.15
            }}
          >
            {materialText}
          </div>
        </div>
      ) : (
        <div style={{ borderBottom: '1px solid #222', marginBottom: '0.1in' }} />
      )}

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.06in'
        }}
      >
        {supplierUrl ? (
          <>
            <QRCodeCanvas
              value={supplierUrl}
              size={128}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#222222"
            />
            <div
              style={{
                fontSize: '5.5pt',
                textTransform: 'uppercase',
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: '100%',
                wordBreak: 'break-all',
                opacity: 0.85
              }}
            >
              Scan for colors &amp; inventory
            </div>
          </>
        ) : (
          <div
            style={{
              fontSize: '8pt',
              textAlign: 'center',
              textTransform: 'uppercase',
              padding: '0.2in',
              border: '1px dashed #999',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            No supplier URL
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleSticker;
